import * as React from 'react';
import { createContext, useEffect, useState } from 'react';

import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';
import leeCoachEpicClient from 'bundles/epic/clients/leeCoach';

// the UI view the user is currently on, `null` represents settings is not shown
type SettingsViews = 'home' | 'studio' | 'profileInfo' | 'memory' | 'chatHistory' | null;

type CoachSettings = {
  /** the settings data saved on the BE for each setting */
  data?: {
    profileInfo?: {
      enabled: boolean;
      fields?: {
        workHistory: { enabled: boolean; complete: boolean };
        educationHistory: { enabled: boolean; complete: boolean };
        learningGoals: { enabled: boolean; complete: boolean };
        locale: { enabled: boolean; complete: boolean };
      };
    };
    memory?: {
      enabled: boolean;
      items: string[];
    };
    chatHistory?: {
      /* TBD */
    };
  };

  /** the current view the user is on */
  view: SettingsViews;

  /** whether settings are being loaded */
  loading: boolean;

  /** update current view */
  updateView: (view: SettingsViews) => void;

  /** update settings data */
  updateData: (data: CoachSettings['data']) => void;
};

const CoachSettingsContext = createContext<CoachSettings>({
  data: {},
  view: null,
  loading: true,
  updateView: () => {},
  updateData: (data) => {},
});

const SettingsContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [settingsData, setSettingsData] = useState<CoachSettings['data']>({});
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<SettingsViews>(null);

  const isCoachSettingsEnabled = leeCoachEpicClient.get('showCoachSettings');

  // Fetch settings from the backend API and update the state
  const fetchSettings = () => {
    try {
      // TODO: make actual gql call here and update settings
      setSettingsData({
        profileInfo: {
          enabled: true,
          fields: {
            educationHistory: {
              enabled: true,
              complete: true,
            },
            workHistory: { enabled: true, complete: false },
            locale: { enabled: true, complete: true },
            learningGoals: { enabled: false, complete: false },
          },
        },
      });

      setLoading(false);
    } catch (error) {
      captureMessageInSentry({ error, source: 'SettingsContextProvider' });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCoachSettingsEnabled) {
      fetchSettings();
    }
  }, [isCoachSettingsEnabled]);

  const updateView = (view: SettingsViews) => {
    setCurrentView(view);
  };

  const updateData = (data: CoachSettings['data']) => {
    setSettingsData(data);
  };

  return (
    <CoachSettingsContext.Provider value={{ data: settingsData, view: currentView, loading, updateView, updateData }}>
      {children}
    </CoachSettingsContext.Provider>
  );
};

export { CoachSettingsContext, SettingsContextProvider };
